import { CdkDrag, CdkDragHandle } from '@angular/cdk/drag-drop'
import { AsyncPipe } from '@angular/common'
import { Component, inject, OnDestroy, OnInit } from '@angular/core'
import { MatButtonModule } from '@angular/material/button'
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog'
import { MatIconModule } from '@angular/material/icon'
import { TranslateModule } from '@ngx-translate/core'
import { BehaviorSubject } from 'rxjs'
import { ToastService } from '../../../toast.service'
import { AiActionService } from './ai-action.service'
import { LLM_LIVE_DEFAULT_TIMEOUT_SECONDS, LLM_LIVE_DEFAULT_WARNING_SECONDS, LlmAction, SentenceIdPair, StreamingChunk } from './ai-action.types'

export type AiActionStreamingDialogData = {
  callId: number
  action: LlmAction
  actionDisplayName: string
  originalContent: string
  sentenceIdPairs: SentenceIdPair[]
  templateItemIds: number[]
}

export type AiActionStreamingDialogResult = {
  generatedText: string
}

@Component({
  selector: 'app-ai-action-streaming-dialog',
  standalone: true,
  imports: [AsyncPipe, CdkDrag, CdkDragHandle, MatButtonModule, MatDialogModule, MatIconModule, TranslateModule],
  templateUrl: './ai-action-streaming-dialog.component.html',
  styleUrl: './ai-action-streaming-dialog.component.scss',
})
export class AiActionStreamingDialogComponent implements OnInit, OnDestroy {
  private dialogRef = inject(MatDialogRef<AiActionStreamingDialogComponent>)
  data: AiActionStreamingDialogData = inject(MAT_DIALOG_DATA)
  private aiActionService = inject(AiActionService)
  private toastService = inject(ToastService)

  generatedText$ = new BehaviorSubject<string>('')
  isComplete$ = new BehaviorSubject<boolean>(false)

  private eventSource: EventSource | null = null
  private cancelTimeoutId: ReturnType<typeof setTimeout> | null = null
  private slowWarningTimeoutId: ReturnType<typeof setTimeout> | null = null

  ngOnInit() {
    this.eventSource = this.aiActionService.sendStreamingRequest(
      this.data.callId,
      this.data.action,
      this.data.sentenceIdPairs,
      this.data.originalContent,
      this.data.templateItemIds
    )

    let isFirstMessageReceived = false

    this.cancelTimeoutId = setTimeout(() => {
      if (!isFirstMessageReceived) {
        this.toastService.show('EDITOR.AI_ACTION_FAILED')
        this.dialogRef.close(null)
      }
    }, LLM_LIVE_DEFAULT_TIMEOUT_SECONDS * 1000)

    this.slowWarningTimeoutId = setTimeout(() => {
      if (!isFirstMessageReceived) {
        this.toastService.show('EDITOR.AI_ACTION_SLOW')
      }
    }, LLM_LIVE_DEFAULT_WARNING_SECONDS * 1000)

    this.eventSource.onmessage = (event: MessageEvent) => {
      let chunk: StreamingChunk
      try {
        chunk = JSON.parse(event.data)
      } catch {
        return
      }
      isFirstMessageReceived = true

      this.clearTimeouts()

      this.generatedText$.next(this.generatedText$.getValue() + chunk.result)

      if (chunk.isFinal) {
        this.eventSource?.close()
        this.isComplete$.next(true)
      }
    }

    this.eventSource.onerror = () => {
      if (this.isComplete$.getValue()) {
        return
      }
      this.clearTimeouts()
      this.eventSource?.close()
      this.toastService.show('EDITOR.AI_ACTION_FAILED')
      this.dialogRef.close(null)
    }
  }

  ngOnDestroy() {
    this.eventSource?.close()
    this.clearTimeouts()
  }

  accept() {
    const result: AiActionStreamingDialogResult = { generatedText: this.generatedText$.getValue() }
    this.dialogRef.close(result)
  }

  discard() {
    this.dialogRef.close(null)
  }

  private clearTimeouts() {
    if (this.cancelTimeoutId !== null) {
      clearTimeout(this.cancelTimeoutId)
      this.cancelTimeoutId = null
    }
    if (this.slowWarningTimeoutId !== null) {
      clearTimeout(this.slowWarningTimeoutId)
      this.slowWarningTimeoutId = null
    }
  }
}
